import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { skillsApi } from "../services/api";

function ScoreBar({ value, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color =
    pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className={`h-2 rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700 w-8 text-left">{Math.round(value)}</span>
    </div>
  );
}

function pct(v) {
  return `${Math.round(v * 100)}%`;
}

function fmt(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}ש`;
  return `${Math.round(sec / 60)}ד`;
}

function fmtCost(usd) {
  if (usd == null) return "—";
  return `$${usd.toFixed(4)}`;
}

// The report carries its own evidence basis: overall_score and resolution_rate are null until at
// least one run has finished, so a report without that flag has no score to show.
const isMeasured = (report) => report?.has_outcome_evidence === true;

function AgentCard({ report }) {
  const topCategories = (report.skill_breakdown || []).slice(0, 4);
  const measured = isMeasured(report);

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-semibold text-gray-800 text-sm">{report.agent_name}</h3>
            {!report.agent_enabled && (
              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">מושבת</span>
            )}
          </div>
          <p className="text-xs text-gray-400">{report.total_runs} ריצות סה"כ</p>
        </div>
        <div className="text-center">
          {measured ? (
            <>
              <div className="text-2xl font-bold text-brand-600">{Math.round(report.overall_score)}</div>
              <div className="text-xs text-gray-400">ציון</div>
            </>
          ) : (
            <>
              <div className="text-sm font-medium text-gray-500">טרם נמדד</div>
              <div className="text-xs text-gray-400">
                {report.terminal_runs === 0 ? "אין ריצות שהסתיימו" : "אין עדיין ציון"}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Score bar */}
      {measured && <ScoreBar value={report.overall_score} />}

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div>
          <div className="font-medium text-gray-700">{measured ? pct(report.resolution_rate) : "טרם נמדד"}</div>
          <div className="text-gray-400">פתרון</div>
        </div>
        <div>
          <div className="font-medium text-gray-700">{fmt(report.avg_duration_s)}</div>
          <div className="text-gray-400">ממוצע זמן</div>
        </div>
        <div>
          <div className="font-medium text-gray-700">{fmtCost(report.avg_cost_usd)}</div>
          <div className="text-gray-400">עלות ממוצע</div>
        </div>
      </div>

      {/* Run counts */}
      <div className="flex gap-3 text-xs">
        <span className="text-green-600">✓ {report.completed_runs}</span>
        <span className="text-red-500">✗ {report.failed_runs}</span>
        {report.pending_runs > 0 && <span className="text-amber-500">⏳ {report.pending_runs}</span>}
      </div>

      {/* Skill breakdown */}
      {measured && topCategories.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-gray-400 font-medium">קטגוריות</p>
          {topCategories.map((s) => (
            <div key={s.category} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 w-24 truncate">{s.category}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-1.5 rounded-full bg-brand-400"
                  style={{ width: `${Math.round(s.completion_rate * 100)}%` }}
                />
              </div>
              <span className="text-xs text-gray-400">{s.total}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs">
        <Link
          to={`/agents/${report.agent_id}/skills`}
          className="text-brand-600 hover:underline"
        >
          מיומנויות העובד ←
        </Link>
        <Link
          to={`/agents/${report.agent_id}`}
          className="text-brand-600 hover:underline"
        >
          פרטי סוכן ←
        </Link>
      </div>
    </div>
  );
}

export default function SkillsPage() {
  const [sinceDays, setSinceDays] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["skills-leaderboard", sinceDays],
    queryFn: () => skillsApi.leaderboard(sinceDays ? { since_days: sinceDays } : {}),
    refetchInterval: 60000,
  });

  const reports = data || [];
  const measuredReports = reports.filter(isMeasured);

  return (
    <div className="max-w-6xl mx-auto space-y-5" dir="rtl">
      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">מיומנויות סוכנים 🎯</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              ציוני ביצוע מחושבים בזמן אמת מנתוני הריצות שהסתיימו. עובד שטרם הסתיימה אצלו ריצה אינו
              מקבל ציון ומוצג כטרם נמדד.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span>טווח נתונים</span>
            <select
              value={sinceDays}
              onChange={(e) => setSinceDays(e.target.value)}
              className="border rounded-lg px-3 py-2 bg-white"
            >
              <option value="">כל הזמן</option>
              <option value="1">היום</option>
              <option value="7">7 ימים אחרונים</option>
              <option value="30">30 ימים אחרונים</option>
              <option value="90">90 ימים אחרונים</option>
            </select>
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : error && !data ? (        // a failed background refetch keeps the loaded leaderboard on screen
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          שגיאה בטעינת נתוני מיומנויות
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-16 text-center text-gray-400">
          <p className="text-3xl mb-3">🎯</p>
          <p>אין עדיין ריצות מושלמות. הנתונים יופיעו לאחר ריצות סוכן ראשונות.</p>
        </div>
      ) : (
        <>
          {/* Summary banner */}
          <div className="bg-brand-50 border border-brand-200 rounded-xl px-5 py-3 flex gap-8 text-sm">
            <div>
              <span className="text-gray-500">סוכנים פעילים: </span>
              <span className="font-semibold text-brand-700">{reports.filter((r) => r.agent_enabled).length}</span>
            </div>
            <div>
              <span className="text-gray-500">ציון ממוצע: </span>
              <span className="font-semibold text-brand-700">
                {measuredReports.length
                  ? Math.round(
                      measuredReports.reduce((s, r) => s + r.overall_score, 0) / measuredReports.length
                    )
                  : "טרם נמדד"}
              </span>
              {measuredReports.length < reports.length && (
                <span className="text-xs text-gray-400">
                  {" "}({reports.length - measuredReports.length} ללא ריצות שהסתיימו)
                </span>
              )}
            </div>
            <div>
              <span className="text-gray-500">ריצות סה"כ: </span>
              <span className="font-semibold text-brand-700">
                {reports.reduce((s, r) => s + r.total_runs, 0)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reports.map((r) => (
              <AgentCard key={r.agent_id} report={r} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
